import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { submitQuiz, fetchProduct } from "../lib/api";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const QUESTIONS = [
  {
    key: "priority",
    q: "What matters most to you?",
    options: [
      { v: "sensation", l: "Skin-on-skin sensation" },
      { v: "texture", l: "Heightened texture & pleasure" },
      { v: "duration", l: "Extending the moment" },
      { v: "variety", l: "Variety to explore" },
    ],
  },
  {
    key: "occasion",
    q: "How would you describe the occasion?",
    options: [
      { v: "everyday", l: "Everyday ritual" },
      { v: "spontaneous", l: "Spontaneous, on-the-go" },
      { v: "special", l: "A special evening" },
      { v: "gift", l: "A thoughtful gift" },
    ],
  },
  {
    key: "scent_pref",
    q: "Your preference on scent / flavor?",
    options: [
      { v: "neutral", l: "Neutral, aroma-balanced" },
      { v: "flavored", l: "Curated flavors" },
      { v: "minimal", l: "Minimal, no fuss" },
    ],
  },
  {
    key: "skin",
    q: "Skin sensitivity?",
    options: [
      { v: "sensitive", l: "Sensitive — gentle, please" },
      { v: "normal", l: "Normal" },
      { v: "no_pref", l: "No preference" },
    ],
  },
];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [altProducts, setAltProducts] = useState([]);
  const { add } = useCart();

  const total = QUESTIONS.length;
  const current = QUESTIONS[step];

  const choose = (v) => {
    const nextAnswers = { ...answers, [current.key]: v };
    setAnswers(nextAnswers);
    if (step < total - 1) {
      setStep(step + 1);
    } else {
      submit(nextAnswers);
    }
  };

  const submit = async (finalAnswers) => {
    setLoading(true);
    try {
      const r = await submitQuiz(finalAnswers);
      setResult(r);
      // fetch alt products
      const alts = await Promise.all(
        (r.alternatives || []).map((id) => fetchProduct(id).catch(() => null))
      );
      setAltProducts(alts.filter(Boolean));
    } catch (e) {
      toast.error("Concierge unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0); setAnswers({}); setResult(null); setAltProducts([]);
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="quiz-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="label-eyebrow mb-4 flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> The BananaX Concierge
          </div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
            Find <span className="italic text-gradient-gold">your</span> signature.
          </h1>
          <p className="text-mist font-light mt-4">Four questions. One curated recommendation.</p>
        </div>

        {/* Progress */}
        {!result && (
          <div className="mb-12">
            <div className="flex justify-between text-xs text-mist mb-2">
              <span>Question {Math.min(step + 1, total)} of {total}</span>
              <span>{Math.round(((step) / total) * 100)}%</span>
            </div>
            <div className="h-px bg-white/10 relative">
              <motion.div
                className="absolute left-0 top-0 h-px bg-gold"
                animate={{ width: `${(step / total) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        )}

        {!result && (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.45 }}
              className="bg-ink-900 border border-white/10 p-10 space-y-8"
              data-testid={`quiz-question-${current.key}`}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-bone leading-tight">
                {current.q}
              </h2>
              <div className="space-y-3">
                {current.options.map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => choose(opt.v)}
                    className="group w-full text-left border border-white/10 hover:border-gold p-5 transition-all flex items-center justify-between"
                    data-testid={`quiz-option-${opt.v}`}
                  >
                    <span className="text-bone font-light">{opt.l}</span>
                    <ArrowRight className="w-4 h-4 text-mist group-hover:text-gold group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-mist hover:text-gold text-xs tracking-[0.22em] uppercase">
                  <ArrowLeft className="w-3 h-3" /> Previous
                </button>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        {loading && (
          <div className="text-center py-16" data-testid="quiz-loading">
            <div className="shimmer-line h-px w-32 mx-auto mb-4"></div>
            <p className="text-mist font-light italic">Our concierge is curating your match…</p>
          </div>
        )}

        {result && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="bg-ink-900 border border-gold/40 p-10 space-y-6"
            data-testid="quiz-result"
          >
            <div className="label-eyebrow">Your match</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-none">
              <span className="italic text-gradient-gold">{result.recommended_product_name}</span>
            </h2>
            <p className="text-mist font-light leading-relaxed text-lg">{result.reasoning}</p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Link to={`/product/${result.recommended_product_id}`} className="btn-primary" data-testid="quiz-shop-recommended-btn">
                View product <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/bundle" className="btn-ghost" data-testid="quiz-build-bundle-btn">
                Build a bundle
              </Link>
              <button onClick={reset} className="btn-ghost" data-testid="quiz-retake-btn">
                Retake quiz
              </button>
            </div>

            {altProducts.length > 0 && (
              <div className="pt-10 border-t border-white/10">
                <div className="label-eyebrow mb-5">You may also love</div>
                <div className="grid sm:grid-cols-2 gap-6">
                  {altProducts.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.slug}`}
                      className="flex gap-4 border border-white/10 p-4 hover:border-gold transition-colors"
                      data-testid={`quiz-alt-${p.id}`}
                    >
                      <div className="w-20 h-24 product-image-wrap shrink-0">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-serif text-lg text-bone">{p.name}</p>
                        <p className="text-mist text-xs italic">{p.tagline}</p>
                        <p className="text-gold text-sm mt-2">₹{p.price}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
